import type { HikCentralImportResult } from '@yawi/types';
import { Button } from '@/core/ui/components';
import { AlertTriangle, CheckCircle, CreditCard, RefreshCw, XCircle } from 'lucide-react';
import { useState } from 'react';

interface HikCentralImportResultsProps {
  result: HikCentralImportResult;
  onReset: () => void;
}

export function HikCentralImportResults({ result, onReset }: HikCentralImportResultsProps) {
  const [showFailed, setShowFailed] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-theme mb-1">Importación completada</h3>
        <p className="text-sm text-theme-secondary">Resumen del proceso de sincronización HikCentral → Yawi</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium text-green-700">Actualizados</span>
          </div>
          <p className="text-2xl font-bold text-green-700">{result.applied}</p>
        </div>

        <div className="p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-4 h-4 text-indigo-600" />
            <span className="text-xs font-medium text-indigo-700">Credenciales RFID</span>
          </div>
          <p className="text-2xl font-bold text-indigo-700">{result.cardsCreated}</p>
        </div>

        <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-xs font-medium text-amber-700">Omitidas</span>
          </div>
          <p className="text-2xl font-bold text-amber-700">{result.skipped}</p>
        </div>

        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800">
          <div className="flex items-center gap-2 mb-1">
            <XCircle className="w-4 h-4 text-red-600" />
            <span className="text-xs font-medium text-red-700">Fallidas</span>
          </div>
          <p className="text-2xl font-bold text-red-700">{result.failed.length}</p>
        </div>
      </div>

      {result.cardsSkipped > 0 && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-amber-50 text-amber-700 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            {result.cardsSkipped} tarjeta{result.cardsSkipped !== 1 ? 's' : ''} omitida{result.cardsSkipped !== 1 ? 's' : ''} — ya existían o se alcanzó el límite de 5 por miembro.
          </span>
        </div>
      )}

      {result.failed.length > 0 && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowFailed((v) => !v)}
            className="text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
          >
            {showFailed ? 'Ocultar errores' : `Ver ${result.failed.length} error${result.failed.length !== 1 ? 'es' : ''}`}
          </button>
          {showFailed && (
            <div className="overflow-x-auto rounded-xl border border-red-200">
              <table className="w-full text-sm">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-red-700">Fila</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-red-700">Razón</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-100">
                  {result.failed.map((f) => (
                    <tr key={f.rowIndex}>
                      <td className="px-4 py-2 text-red-700">{f.rowIndex + 1}</td>
                      <td className="px-4 py-2 text-red-600">{f.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Button variant="outline" leftIcon={<RefreshCw className="w-4 h-4" />} onClick={onReset}>
        Importar otro archivo
      </Button>
    </div>
  );
}
